// Percorso file: src/pages/GamesListPage.jsx
//
// LA LISTA PARTITE DI UNA SQUADRA — cantiere GAMES, fetta G1 (ossatura),
// PIANO-GAMES §2 fase 1 e §5.2.
//
// Mostra, per UNA squadra e per il solo ANNO OPERATIVO, le partite del
// calendario — gli eventi di tipo «Game» di quella squadra — in UN'UNICA
// LISTA in ordine cronologico (la più futura in alto, la più vecchia in
// fondo), con lo stato di ciascuna DECISO DALLA DATA (G3, non più dal
// cassetto): «Played» se di un giorno anteriore a oggi, «Next» la più
// vicina fra quelle di oggi o future, «To play» le altre future. Il
// risultato compare solo se nel cassetto `games` c'è il record di quella
// partita, qualunque sia lo stato. All'arrivo la pagina si posiziona da
// sola sulla NEXT, che è evidenziata nella sua posizione naturale.
//
// ⚠️ LA SQUADRA ARRIVA DAL PARAMETRO DI ROTTA, non si deduce qui: la sceglie
// l'allenatore nella SUA home (HomeCoach, `squadraScelta`) e viaggia in
// `/games/team/:teamName`, esattamente come per `/attendance/team/:teamName`.
// Niente selettore in pagina, niente `location.state`, niente «prima tessera
// del coach». (`activeContext` per un coach porta la spia `"TEAMTD"`, non
// una squadra: serve al solo cappello di ruolo.)
//
// ⚠️ IL GATE STA A MONTE, ed è un COMPONENTE ESTERNO, per lo stesso motivo di
// AttendanceSummary.jsx: in React gli hook non si saltano con un `if`, e un
// gate dentro la pagina — dopo gli hook — farebbe partire le chiamate anche a
// chi non deve vederla. Il componente esportato non ha effetti e non nomina
// `api`. ADMIN e SUPERADMIN senza cappello COACH sono respinti qui (PIANO §5.6:
// «un admin senza tessera coach non entra, per ora»).
//
// ⚠️ DUE LETTURE, ENTRAMBE ESISTENTI O GIÀ SCRITTE (nessuna rotta nuova qui):
//   · `GET /events` — nudo, filtro nel browser, come ogni lettore di eventi
//     del frontend (MyCalendar, AttendanceSummary);
//   · `GET /games/team/:teamName` — le partite SALVATE della squadra
//     nell'anno operativo (backend F1), un array di record
//     `{ eventId, team, date, season, score{us,them}, players[], updatedAt }`,
//     riservata ai coach di quella squadra.
// Il criterio «è una partita» è `ev.type === "Game"`, AFFERMATIVO: è una
// decisione del committente (incarico G1 §6.1) e NON il «non Training» che
// il calendario usa per il colore delle card — un evento «Event» qui non entra.
//
// ⚠️ L'ANNO È L'ANNO OPERATIVO del club (`operatingSeason` di useAuth, intero,
// `null` fuori da `seasonStatus === "ready"`), e l'anno di un evento si legge
// dalla sua data: gli eventi NON portano una stagione (RICOG-GAMES-01 §A.2).
// Finché l'anno non è noto la pagina resta in caricamento e NON filtra a
// vuoto: una lista vuota per un anno `null` direbbe una cosa falsa.
//
// ⚠️ NIENTE `<Link>`: si naviga da un `<button>` con `navigate`, come in tutte
// le pagine ricostruite. E NIENTE storage locale: qui non si scrive nulla sul
// dispositivo. Lo stato «in corso» del PIANO (§2) NON ESISTE su disco
// (decisione del committente in G2, «Caso A»: la partita in corso vive solo
// in memoria, nella pagina della partita). L'APERTURA non guarda lo stato
// per data: con record nel cassetto → riepilogo (modificabile), senza →
// preparazione (fetta G2), sulla stessa rotta: è la pagina della partita a
// leggere il record e a scegliere la fase. Così una «Played» mai registrata
// si può ancora aprire e registrare.
//
// IL VECCHIO NON C'È PIÙ, LATO FRONTEND: le pagine GameCallUps /
// GameHistory, le loro rotte e il bottone «Game Call Ups» di
// AttendanceSummary sono stati tolti (98b5be7). Restano le rotte del server
// e i dati (`gamecallups`), che questa pagina non usa.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import HomeShell from "../components/HomeShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { isActingAs } from "../utils/roleUtils";
import { eDiUnaMiaSquadra } from "../utils/squadre";
import { dataEvento, evTimeStamp } from "../utils/calendario";
// La pastiglia §2 (`.qpsc-pill`) vive in AdminTools.css: si importa il foglio
// per dichiararne la dipendenza, come fa EnrolNextSeason.jsx.
import "../components/AdminTools.css";
import "./Games.css";

// La home del coach: il ripiego del «Back» della lista, che è il solo posto
// da cui la lista si apre (ROLE_ROUTES, auth/access.js: COACH → /home/coach).
const HOME_COACH = "/home/coach";

// ===========================================================================
// AIUTANTI DI DATA — `dataEvento` ed `evTimeStamp` dalla sede unica
// utils/calendario.js (giro G-K1)
// ===========================================================================

/** Giorno + data brevi, la stessa forma delle card del calendario. */
function etichettaBreve(ev) {
  const d = dataEvento(ev);
  if (!d) return String(ev?.date || "");

  return `${d.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()} ${d.toLocaleDateString(
    "en-AU",
    { day: "2-digit", month: "2-digit" }
  )}`;
}

/** L'anno di un evento, dalla sua data (gli eventi non portano la stagione). */
function annoEvento(ev) {
  const d = dataEvento(ev);
  return d ? d.getFullYear() : null;
}

/** Il criterio «è una partita» di GAMES: affermativo, deciso dal committente. */
const ePartita = (ev) => ev && ev.type === "Game";

// ===========================================================================
// LO STATO PER DATA (G3) — lo decide il GIORNO della partita rispetto a
// oggi, non il cassetto. Funzioni PURE: «oggi» è un parametro (ms), così il
// banco le prova con un giorno inventato (docs/cantieri/games/banco/).
// ===========================================================================

const STATO_PARTITA = Object.freeze({
  PLAYED: "played",
  NEXT: "next",
  TOPLAY: "toplay"
});

/** L'inizio del giorno (ora azzerata) di un istante, in ms: il confronto
 *  è per GIORNO, non per ora — una partita di oggi non è mai «giocata». */
function inizioGiorno(istante) {
  const d = new Date(istante);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Lo stato per data di OGNI partita della lista: `{ [id]: stato }`.
 *    giorno anteriore a oggi            → PLAYED;
 *    la più vicina fra oggi e le future → NEXT (una sola);
 *    le altre di oggi o future          → TOPLAY.
 *  Senza partite future non c'è nessuna NEXT (tutte PLAYED). Una data
 *  illeggibile non è «passata» (resta TOPLAY) ma non può essere la NEXT. */
function statiPerData(partite, oggi) {
  const soglia = inizioGiorno(oggi);
  const stati = {};
  let prossima = null;

  for (const ev of partite) {
    const d = dataEvento(ev);
    const id = String(ev.id);
    if (d && d.getTime() < soglia) {
      stati[id] = STATO_PARTITA.PLAYED;
      continue;
    }
    stati[id] = STATO_PARTITA.TOPLAY;
    if (d && (prossima === null || evTimeStamp(ev) < evTimeStamp(prossima))) {
      prossima = ev;
    }
  }

  if (prossima) stati[String(prossima.id)] = STATO_PARTITA.NEXT;
  return stati;
}

/** L'etichetta a schermo dello stato (la pastiglia la rende maiuscola:
 *  NEXT / TO PLAY / PLAYED). */
const ETICHETTA_STATO = Object.freeze({
  [STATO_PARTITA.PLAYED]: "Played",
  [STATO_PARTITA.NEXT]: "Next",
  [STATO_PARTITA.TOPLAY]: "To play"
});

// ===========================================================================
// IL CANCELLO — componente esterno, senza effetti e senza chiamate
// ===========================================================================
export default function GamesListPage() {
  const { user, activeContext } = useAuth() || {};
  const { teamName } = useParams();
  const squadra = String(teamName || "").trim();

  // «Indossa ADESSO il cappello COACH», col vocabolario canonico: MANAGER,
  // TD e MANAGERTD valgono COACH (ROLE_EQUIVALENTS, auth/access.js).
  const indossaCoach = isActingAs(user, activeContext, "COACH");

  if (!user || !indossaCoach) {
    return (
      <HomeShell backTo="/home" showSwitch={false}>
        <h1 className="qpsc-page-title">Games</h1>
        <p className="qpsc-body-text">Unauthorized access.</p>
      </HomeShell>
    );
  }

  // Indirizzo digitato senza squadra: si dice dove si sceglie, e NON si
  // indovina una squadra al posto suo. Nessuna chiamata parte.
  if (!squadra) {
    return (
      <HomeShell backTo={HOME_COACH} showSwitch={false}>
        <h1 className="qpsc-page-title">Games</h1>
        <p className="qpsc-body-text">
          Open a team from your home page to see its games.
        </p>
      </HomeShell>
    );
  }

  return <ElencoPartite squadra={squadra} />;
}

// ===========================================================================
// LA PAGINA — montata solo per chi ha superato il cancello
// ===========================================================================
function ElencoPartite({ squadra }) {
  const navigate = useNavigate();
  const { operatingSeason } = useAuth() || {};
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  const [events, setEvents] = useState([]);
  const [records, setRecords] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState("");

  // «Oggi» per lo stato per data: letto UNA volta all'arrivo in pagina (G3).
  // Non si rilegge a ogni render: la lista non cambia stato sotto gli occhi.
  const [oggi] = useState(() => Date.now());

  // La riga della NEXT e la spia «ho già scrollato»: lo scroll automatico
  // avviene UNA volta sola per lista, all'arrivo, e non insegue l'utente.
  const nextRef = useRef(null);
  const scrollato = useRef(false);

  // -----------------------------------------------------------------------
  // I DATI — le due letture insieme, in un solo effetto. La guardia `vivo`
  // (AttendanceSummary.jsx) copre sia lo smontaggio sia il cambio di squadra
  // a componente montato: una risposta arrivata fuori tempo non scrive
  // nulla. Si controlla PRIMA di ogni scrittura di stato, non solo alla fine.
  // -----------------------------------------------------------------------
  useEffect(() => {
    let vivo = true;

    setCaricamento(true);
    setErrore("");
    scrollato.current = false;

    Promise.all([
      api.get("/events"),
      api.get(`/games/team/${encodeURIComponent(squadra)}`)
    ])
      .then(([tuttiGliEventi, partiteSalvate]) => {
        if (!vivo) return;
        setEvents(Array.isArray(tuttiGliEventi) ? tuttiGliEventi : []);
        setRecords(Array.isArray(partiteSalvate) ? partiteSalvate : []);
        setErrore("");
      })
      .catch(() => {
        // Rifiuto e guasto di rete finiscono nello stesso ramo, di proposito:
        // distinguerli direbbe a un estraneo quali squadre esistono. E se
        // una sola delle due letture fallisce la lista NON si mostra a metà:
        // senza i record ogni partita parrebbe «da giocare», senza gli
        // eventi la squadra parrebbe senza partite — entrambe cose false.
        if (!vivo) return;
        setEvents([]);
        setRecords([]);
        setErrore("This team's games are not available.");
      })
      .finally(() => {
        if (vivo) setCaricamento(false);
      });

    return () => {
      vivo = false;
    };
  }, [squadra]);

  // -----------------------------------------------------------------------
  // LE PARTITE DA MOSTRARE — un evento entra se è della squadra (metro di
  // casa `eDiUnaMiaSquadra`, che normalizza i DUE lati e NON comprende la
  // sentinella `"all"`), se è un «Game» (affermativo) e se la sua data cade
  // nell'anno operativo. Poi lo STATO lo decide la DATA (`statiPerData`,
  // G3); il cassetto serve solo al risultato: con record → si mostra, senza
  // → niente (una «Played» mai registrata non ha risultato, ed è giusto).
  // La chiave è l'id dell'evento, che in produzione è sempre uguale all'id
  // del documento (PIANO §4) ed è ciò che il backend scrive in `eventId`.
  // -----------------------------------------------------------------------
  const recordPerEvento = useMemo(() => {
    const mappa = new Map();
    for (const r of records) {
      if (r && r.eventId != null) mappa.set(String(r.eventId), r);
    }
    return mappa;
  }, [records]);

  const partite = useMemo(() => {
    if (annoOperativo == null) return [];

    const mie = events.filter(
      (ev) =>
        ePartita(ev) &&
        eDiUnaMiaSquadra(ev.team, [squadra]) &&
        annoEvento(ev) === annoOperativo
    );

    // UN'UNICA lista in vero ordine cronologico: la più futura in alto, la
    // più vecchia in fondo (G3). La NEXT resta nella sua posizione naturale
    // — sopra le più future, sotto tutte le passate — e non si strappa in
    // cima: è lo scroll automatico a portarla sotto gli occhi.
    mie.sort((a, b) => evTimeStamp(b) - evTimeStamp(a));

    const stati = statiPerData(mie, oggi);
    return mie.map((ev) => ({
      ev,
      record: recordPerEvento.get(String(ev.id)) || null,
      stato: stati[String(ev.id)]
    }));
  }, [events, recordPerEvento, squadra, annoOperativo, oggi]);

  // Con record → riepilogo; senza → preparazione, QUALUNQUE sia lo stato
  // per data. È la STESSA rotta: la pagina della partita legge il cassetto
  // e riparte dalla fase giusta (G2 §4), così il ricaricamento e il link
  // diretto fanno lo stesso.
  const apriPartita = (ev) =>
    navigate(
      `/games/team/${encodeURIComponent(squadra)}/prepare/${encodeURIComponent(String(ev.id))}`
    );

  // Finché l'anno operativo non è noto si resta in caricamento: non si
  // filtra a vuoto (incarico G1 §6.2).
  const inAttesa = caricamento || annoOperativo == null;
  const vuota = partite.length === 0;

  // -----------------------------------------------------------------------
  // LO SCROLL SULLA NEXT — una volta sola, quando la lista è pronta (G3).
  // La riga è agganciata dal `ref` della card NEXT nello stesso commit in
  // cui `inAttesa` cade, quindi qui è già nel DOM. Senza NEXT (nessuna
  // partita futura) non si scrolla: la lista resta in cima. `scrollIntoView`
  // al centro, come Attendance.jsx fa per portare un evento sotto gli occhi;
  // istantaneo, non animato, perché all'arrivo non c'è un gesto da seguire.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (inAttesa || scrollato.current) return;
    scrollato.current = true;
    const el = nextRef.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "center" });
    }
  }, [inAttesa]);

  return (
    <HomeShell backTo={HOME_COACH} showSwitch={false}>
      <h1 className="qpsc-page-title">Games</h1>

      <div className="qpsc-games-context">
        <span className="qpsc-games-context__team">{squadra}</span>
      </div>

      {errore && <p className="qpsc-games-error">{errore}</p>}

      {/* L'errore vince sull'attesa: se il backend ha risposto con un
          rifiuto (anche il 409 di un anno operativo illeggibile) non ha
          senso dire «Loading…» sotto. */}
      {errore ? null : inAttesa ? (
        <p className="qpsc-games-empty">Loading games…</p>
      ) : vuota ? (
        <p className="qpsc-games-empty">No games for this team this season.</p>
      ) : (
        <div className="qpsc-games-cards">
          {partite.map(({ ev, record, stato }) => (
            <CardPartita
              key={ev.id}
              ev={ev}
              record={record}
              stato={stato}
              cardRef={stato === STATO_PARTITA.NEXT ? nextRef : null}
              onApri={() => apriPartita(ev)}
            />
          ))}
        </div>
      )}
    </HomeShell>
  );
}

// ===========================================================================
// LA CARD DI UNA PARTITA — un `<button>` sempre acceso (G2): con record →
// riepilogo, senza → preparazione. Data, avversario, la pastiglia dello
// stato PER DATA (G3: Next / To play / Played) e, solo se c'è il record,
// il risultato. La NEXT porta il modificatore `--next` (evidenza nella sua
// posizione) e il `ref` con cui la pagina la porta sotto gli occhi.
// ===========================================================================
function CardPartita({ ev, record, stato, cardRef, onApri }) {
  const next = stato === STATO_PARTITA.NEXT;
  const punteggio =
    record && record.score
      ? `${record.score.us ?? "–"}–${record.score.them ?? "–"}`
      : "";

  const contenuto = (
    <>
      <span className="qpsc-games-card__when">
        {etichettaBreve(ev)}
        {ev.time ? ` ${ev.time}` : ""}
      </span>

      {ev.opponent && (
        <span className="qpsc-games-card__opponent">vs {ev.opponent}</span>
      )}

      <span className="qpsc-games-card__status">
        <span className={`qpsc-pill qpsc-games-status--${stato}`}>
          {ETICHETTA_STATO[stato]}
        </span>
        {punteggio && <span className="qpsc-games-card__score">{punteggio}</span>}
      </span>
    </>
  );

  return (
    <button
      type="button"
      ref={cardRef}
      className={"qpsc-games-card" + (next ? " qpsc-games-card--next" : "")}
      onClick={onApri}
    >
      {contenuto}
    </button>
  );
}
